import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Project } from '../types/project';
import { projects } from '../data/projects';
import { BackButton } from '../components/projects/BackButton';
import { ScrollButton } from '../components/projects/ScrollButton';
import { GalleryImage } from '../components/projects/GalleryImage';

import { ProjectHeader } from '../components/hero/ProjectHeader';
import ProjectDetailsSkeleton from '../components/loading/ProjectDetailsSkeleton';
import ImageLightbox from '../components/projects/ImageLightBox';
import { ScrollInstruction } from '@/components/projects/ScrollInstruction';

interface LightboxState {
  isOpen: boolean;
  currentImageIndex: number;
}

// Gallery Component
const ProjectGallery = ({
  images,
  onImageClick,
}: {
  images: Project['images'];
  onImageClick: (index: number) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = direction === 'left' ? -500 : 500;
      containerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (images.length === 0) return null;

  return (
    <section className="mt-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Project Gallery
        </h2>

        <div className="relative group">
          <ScrollButton direction="left" onClick={() => handleScroll('left')} />
          <ScrollButton
            direction="right"
            onClick={() => handleScroll('right')}
          />

          <div
            ref={containerRef}
            className="overflow-x-auto scrollbar-hide relative"
          >
            <div className="inline-flex gap-6 pb-4">
              {images.map((image, index) => (
                <GalleryImage
                  key={index}
                  url={image.url}
                  caption={image.caption}
                  onClick={() => onImageClick(index)}
                />
              ))}
            </div>
          </div>
        </div>

        <ScrollInstruction />
      </div>
    </section>
  );
};

export default function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<LightboxState>({
    isOpen: false,
    currentImageIndex: 0,
  });

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        const foundProject = projects.find(p => p.id === id);
        setProject(foundProject || null);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  const handleImageClick = (index: number) => {
    setLightbox({ isOpen: true, currentImageIndex: index });
  };

  const handleCloseLightbox = () => {
    setLightbox(prev => ({ ...prev, isOpen: false }));
  };

  const handlePreviousImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentImageIndex: Math.max(0, prev.currentImageIndex - 1),
    }));
  };

  const handleNextImage = () => {
    if (!project) return;
    setLightbox(prev => ({
      ...prev,
      currentImageIndex: Math.min(
        project.images.length - 1,
        prev.currentImageIndex + 1
      ),
    }));
  };

  if (loading) return <ProjectDetailsSkeleton />;

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Project Not Found</h2>
          <button
            onClick={() => navigate('/')}
            className="text-blue-500 hover:text-blue-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 mt-4 bg-white dark:bg-gray-900">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="container mx-auto px-4 py-8 max-w-4xl"
      >
        <BackButton onClick={() => navigate('/')} />
        <ProjectHeader project={project} />

        <ProjectGallery
          images={project.images}
          onImageClick={handleImageClick}
        />

        <ImageLightbox
          images={project.images}
          currentIndex={lightbox.currentImageIndex}
          isOpen={lightbox.isOpen}
          onClose={handleCloseLightbox}
          onPrevious={handlePreviousImage}
          onNext={handleNextImage}
        />
      </motion.div>
    </div>
  );
}
